import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiPlay, HiLocationMarker, HiClock, HiMenu, HiX } from 'react-icons/hi';
import { IoRadioButtonOn } from 'react-icons/io5';
import './FieldStyle.css';

// FIELD — Editorial magazine layout. Think ESPN The Magazine meets Netflix.
export default function FieldStyle({ config }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/games`);
      const data = await res.json();
      setGames(data.data.games || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const liveGames = games.filter(g => g.status === 'live');
  const upcomingGames = games.filter(g => g.status === 'scheduled' || g.status === 'ready');
  const recordedGames = games.filter(g => g.status === 'recorded' && g.recordingUrl);
  const featured = liveGames[0] || upcomingGames[0];

  if (loading) return <div className="field-loading"><span>FIELD</span></div>;

  return (
    <div className="field">

      {/* Header */}
      <header className="field-header">
        <div className="field-header-inner">
          <div className="field-logo">
            <span className="field-logo-mark">F</span>
            <span className="field-logo-text">FIELD</span>
          </div>
          <nav className={`field-nav ${menuOpen ? 'open' : ''}`}>
            <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/schedule" onClick={() => setMenuOpen(false)}>Schedule</Link>
            <Link to="/replays" onClick={() => setMenuOpen(false)}>Replays</Link>
            <Link to="/login" className="field-nav-cta" onClick={() => setMenuOpen(false)}>Sign In</Link>
          </nav>
          <button className="field-burger" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <HiX /> : <HiMenu />}
          </button>
        </div>
      </header>

      {/* Featured Hero — full bleed */}
      {featured && (
        <section className="field-hero">
          <div className="field-hero-bg">
            {featured.thumbnailUrl && <img src={featured.thumbnailUrl} alt="" />}
          </div>
          <div className="field-hero-overlay" />
          <div className="field-hero-content">
            <div className="field-hero-meta">
              {featured.status === 'live' && (
                <div className="field-live-pill">
                  <IoRadioButtonOn className="field-live-dot" />
                  LIVE NOW
                </div>
              )}
              <span className="field-hero-sport">{featured.sport?.toUpperCase()}</span>
            </div>
            <h1 className="field-hero-title">
              {featured.homeTeam}<br />
              <span className="field-vs">vs</span><br />
              {featured.awayTeam}
            </h1>
            <div className="field-hero-info">
              <HiLocationMarker /> {featured.venue}
              {featured.status !== 'live' && (
                <><HiClock /> {new Date(featured.scheduledStart).toLocaleString('en-US', { 
                  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' 
                })}</>
              )}
            </div>
            {featured.status === 'live' && (
              <Link to={`/watch/${featured.gameId}`} className="field-hero-btn">
                <HiPlay /> Watch Live
              </Link>
            )}
          </div>
        </section>
      )}

      <div className="field-body">

        {/* Live strip */}
        {liveGames.length > 1 && (
          <section className="field-section">
            <h2 className="field-section-label"><IoRadioButtonOn className="field-live-dot" /> Live Now</h2>
            <div className="field-strip">
              {liveGames.slice(1).map(game => (
                <Link key={game.gameId} to={`/watch/${game.gameId}`} className="field-strip-card">
                  <div className="field-strip-sport">{game.sport?.toUpperCase()}</div>
                  <div className="field-strip-teams">{game.homeTeam} <em>vs</em> {game.awayTeam}</div>
                  <div className="field-strip-venue"><HiLocationMarker /> {game.venue}</div>
                  <div className="field-strip-watch"><HiPlay /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming — editorial asymmetric grid */}
        {upcomingGames.length > 0 && (
          <section className="field-section">
            <h2 className="field-section-label">Upcoming</h2>
            <div className="field-editorial-grid">
              {upcomingGames.slice(0, 5).map((game, i) => (
                <div key={game.gameId} className={`field-editorial-card ${i === 0 ? 'featured' : ''}`}>
                  <div className="field-card-sport">{game.sport?.toUpperCase()}</div>
                  <h3 className="field-card-teams">
                    {game.homeTeam} <span>vs</span> {game.awayTeam}
                  </h3>
                  <div className="field-card-meta">
                    <span><HiLocationMarker /> {game.venue}</span>
                    <span><HiClock /> {new Date(game.scheduledStart).toLocaleString('en-US', { 
                      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' 
                    })}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Replays — horizontal scroll */}
        {recordedGames.length > 0 && (
          <section className="field-section">
            <h2 className="field-section-label">Replays</h2>
            <div className="field-replays">
              {recordedGames.map(game => (
                <Link key={game.gameId} to={`/watch/${game.gameId}`} className="field-replay-card">
                  <div className="field-replay-thumb">
                    {game.thumbnailUrl
                      ? <img src={game.thumbnailUrl} alt="Match" />
                      : <div className="field-replay-thumb-placeholder" />
                    }
                    <div className="field-replay-play"><HiPlay /></div>
                  </div>
                  <div className="field-replay-info">
                    <div className="field-card-sport">{game.sport?.toUpperCase()}</div>
                    <div className="field-replay-teams">{game.homeTeam} vs {game.awayTeam}</div>
                    <div className="field-replay-venue"><HiLocationMarker /> {game.venue}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
}
